import { useState, useEffect, useCallback } from "react";
import lurneaxLogo from "@/assets/lurneax-logo.svg";
import { supabase } from "@/integrations/supabase/client";
import { invokeFunction } from "@/lib/invokeFunction";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { z } from "zod";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { AnimatedGreetingOverlay } from "@/components/ui/animated-greeting-overlay";
import { RoleSelectionDialog } from "@/components/RoleSelectionDialog";
import { USER_SESSION_QUERY_KEY, type UserSessionData } from "@/hooks/use-user-session";

const loginSchema = z.object({
  email: z.string().trim().email({ message: "Please enter a valid email address" }).max(255, { message: "Email must be less than 255 characters" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }).max(100, { message: "Password must be less than 100 characters" }),
});

const ACTIVE_ROLE_KEY = "lurneax_active_role";

const getRedirectPathForRole = (role: string): string => {
  if (role === 'parent') return '/parent-dashboard';
  if (role === 'student') return '/assessment/dashboard';
  if (role === 'master_admin' || role === 'admin' || role === 'state_partner') return '/national-dashboard';
  return '/learning/dashboard';
};

// Shape a get_my_context RPC payload into the exact object useUserSession caches.
function shapeSessionFromContext(userId: string, email: string | null, ctx: any): UserSessionData {
  const profile = (ctx?.profile ?? null) as any;
  const roles = Array.isArray(ctx?.roles) ? (ctx.roles as string[]) : [];
  const isMasterAdmin = roles.includes("master_admin");
  const isAdmin = roles.includes("admin") || isMasterAdmin;
  const isCenterManager = roles.includes("center_manager");
  const isStatePartner = roles.includes("state_partner");
  const isGrowthPartner = roles.includes("growth_partner");

  const rawLocations = Array.isArray(ctx?.locations) ? ctx.locations : [];
  const locations = rawLocations.map((l: any) => ({
    location_id: l.location_id as string,
    locations:
      l && (l.name !== undefined || l.state !== undefined)
        ? { name: (l.name ?? "") as string, state: (l.state ?? null) as string | null }
        : null,
  })) as UserSessionData["locations"];
  const locationIds = locations.map((l) => l.location_id);
  const locationState =
    ((ctx?.location_state as string | null) ?? null) ||
    (locations[0]?.locations as any)?.state ||
    null;
  const assignedState = (profile?.assigned_state as string | null) ?? null;
  const userState = (isStatePartner ? assignedState : null) || locationState;

  return {
    userId,
    projectId: (profile?.project_id as string | null) ?? null,
    projectName: (ctx?.project_name as string | null) ?? null,
    assignedState,
    roles,
    locations,
    locationIds,
    userState,
    role: isMasterAdmin ? "master_admin" : roles[0] || null,
    isAdmin,
    isMasterAdmin,
    isCenterManager,
    isStatePartner,
    isGrowthPartner,
    fullName: (profile?.full_name as string | null) ?? null,
    email: (profile?.email as string | null) ?? email,
    userType: (profile?.user_type as string | null) ?? null,
    avatarUrl: (profile?.avatar_url as string | null) ?? null,
    phone: (profile?.phone as string | null) ?? null,
    profileCompleted: (profile?.profile_completed as boolean | null) ?? null,
    tourCompleted: (profile?.tour_completed as boolean | null) ?? null,
  };
}

const LandingPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);

  const handleRoleSelectedAndRedirect = useCallback((role: string) => {
    localStorage.setItem(ACTIVE_ROLE_KEY, role);
    setShowRoleSelection(false);
    navigate(getRedirectPathForRole(role));
  }, [navigate]);

  const checkRolesAndRedirect = useCallback(async (userId: string, userEmail: string | null) => {
    // Single RPC replaces the previous user_roles fetch — also primes the
    // shared useUserSession cache so the destination page renders without
    // a second round-trip.
    const { data: ctxRaw } = await supabase.rpc("get_my_context" as any);
    const ctx = (ctxRaw ?? {}) as any;
    const userRoles: string[] = Array.isArray(ctx?.roles) ? (ctx.roles as string[]) : [];

    // Prime react-query cache with the exact shape useUserSession returns.
    queryClient.setQueryData(
      USER_SESSION_QUERY_KEY,
      shapeSessionFromContext(userId, userEmail, ctx),
    );

    if (userRoles.length === 0) { navigate('/learning/dashboard'); return; }
    const storedRole = localStorage.getItem(ACTIVE_ROLE_KEY);
    if (storedRole && !userRoles.includes(storedRole)) {
      // Stale role (e.g. account was upgraded) — clear it before redirect
      localStorage.removeItem(ACTIVE_ROLE_KEY);
    }
    if (storedRole && userRoles.includes(storedRole)) { navigate(getRedirectPathForRole(storedRole)); return; }
    if (userRoles.length === 1) { handleRoleSelectedAndRedirect(userRoles[0]); return; }
    setAvailableRoles(userRoles);
    setShowRoleSelection(true);
    setIsLoading(false);
  }, [navigate, handleRoleSelectedAndRedirect, queryClient]);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) await checkRolesAndRedirect(session.user.id, session.user.email ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        setTimeout(async () => { await checkRolesAndRedirect(session.user.id, session.user.email ?? null); }, 0);
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate, checkRolesAndRedirect]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    try { loginSchema.parse({ email, password }); } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: { email?: string; password?: string } = {};
        error.errors.forEach((err) => {
          if (err.path[0] === 'email') fieldErrors.email = err.message;
          if (err.path[0] === 'password') fieldErrors.password = err.message;
        });
        setErrors(fieldErrors);
        return;
      }
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) {
        setIsLoading(false);
        if (error.message.includes("Invalid login credentials")) toast.error("Invalid email or password");
        else if (error.message.includes("Email not confirmed")) toast.error("Please confirm your email address");
        else toast.error(error.message);
        return;
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to sign in");
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    try { z.string().trim().email().parse(email); } catch { setErrors({ email: "Please enter a valid email address" }); return; }
    setIsLoading(true);
    try {
      const response = await invokeFunction('send-password-reset', { requireAuth: false, body: { email: email.trim(), publicReset: true } });
      if (response.error || response.data?.error) { toast.error(response.error?.message || response.data?.error || "Failed to send reset email"); return; }
      toast.success("Password reset email sent! Check your inbox.");
      setIsForgotPassword(false);
      setEmail("");
    } catch (error: any) {
      toast.error(error.message || "Failed to send reset email");
    } finally { setIsLoading(false); }
  };

  return (
    <>
      <AnimatedGreetingOverlay isVisible={isLoading && !isForgotPassword && !showRoleSelection} />
      <RoleSelectionDialog open={showRoleSelection} roles={availableRoles} onSelectRole={handleRoleSelectedAndRedirect} />
      
      <main className="min-h-screen relative overflow-hidden flex items-center justify-center" style={{ background: '#FDF0E8', fontFamily: "'Nunito', sans-serif" }}>
        {/* Animated clay blobs */}
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
          <div className="absolute animate-clay-morph" style={{
            width: 500, height: 420,
            background: 'linear-gradient(135deg, #FFBFA0, #FF9870)',
            boxShadow: 'inset -25px -25px 50px rgba(180,60,20,0.12), inset 12px 12px 24px rgba(255,255,255,0.5)',
            top: -160, right: -120,
            opacity: 0.65,
            borderRadius: '60% 40% 50% 50% / 50% 55% 45% 50%',
          }} />
          <div className="absolute animate-clay-morph-delayed" style={{
            width: 360, height: 320,
            background: 'linear-gradient(135deg, #FFE0C8, #FFCFB3)',
            boxShadow: 'inset -16px -16px 35px rgba(180,80,30,0.1), inset 8px 8px 18px rgba(255,255,255,0.55)',
            bottom: -100, left: -80,
            opacity: 0.7,
            borderRadius: '45% 55% 40% 60% / 60% 40% 55% 45%',
          }} />
          <div className="absolute animate-clay-morph" style={{
            width: 220, height: 200,
            background: 'linear-gradient(135deg, #FFD4A8, #FFBA80)',
            boxShadow: 'inset -10px -10px 22px rgba(160,60,10,0.1), inset 6px 6px 14px rgba(255,255,255,0.5)',
            top: '55%', left: '10%',
            opacity: 0.5,
            borderRadius: '55% 45% 60% 40% / 45% 55% 50% 50%',
          }} />
          <div className="absolute animate-clay-morph-delayed" style={{
            width: 180, height: 160,
            background: 'linear-gradient(135deg, #A8E8C8, #70D4A8)',
            boxShadow: 'inset -8px -8px 18px rgba(20,100,60,0.12), inset 5px 5px 12px rgba(255,255,255,0.5)',
            top: '12%', left: '42%',
            opacity: 0.45,
            borderRadius: '50% 50% 45% 55% / 55% 45% 60% 40%',
          }} />
        </div>

        {/* Main layout */}
        <div className="relative z-10 w-full max-w-[960px] mx-6 my-6 grid grid-cols-1 lg:grid-cols-2 gap-5 items-center animate-in fade-in slide-in-from-bottom-6 duration-700">
          
          {/* LEFT COLUMN */}
          <div className="hidden lg:flex flex-col gap-2">

            {/* Logo centered on top */}
            <div className="flex justify-center -mb-1">
              <img src={lurneaxLogo} alt="Lurneax" className="h-[98px] w-[98px]" />
            </div>

            {/* Headline card */}
            <div className="relative overflow-hidden rounded-[28px] px-7 py-8" style={{
              background: '#FFF8F2',
              boxShadow: '0 14px 44px rgba(160,80,40,0.1), inset 0 2px 0 rgba(255,255,255,0.9)',
            }}>
              <div className="absolute -top-16 -right-16 w-[200px] h-[180px] rounded-full pointer-events-none" style={{
                background: 'radial-gradient(ellipse, rgba(244,67,33,0.1) 0%, transparent 65%)',
                filter: 'blur(30px)',
              }} />
              <h1 className="text-5xl font-bold italic leading-none tracking-tight mb-3" style={{ fontFamily: "'Fraunces', serif", color: '#2A1810', letterSpacing: '-2px' }}>
                Where kids<br /><span style={{ color: '#F44321' }}>love</span> learning.
              </h1>
              <p className="text-sm font-semibold leading-relaxed max-w-[300px]" style={{ color: '#8A6050' }}>
                Australia's tutoring franchise LMS — built warm and human, for learners.
              </p>
            </div>

            {/* Feature cards grid */}
            <div className="grid grid-cols-2 gap-3">
              <FeatureCard emoji="🎮" title="On-Demand Gaming" subtitle="Learn through play" colorClass="text-[#F44321]" bgClass="bg-[#F44321]/10" />
              <FeatureCard emoji="⚡" title="Microlearning" subtitle="Bite-sized lessons" colorClass="text-[#2D9B6F]" bgClass="bg-[#2D9B6F]/10" />
              <FeatureCard emoji="🃏" title="Flashcards" subtitle="Spaced repetition" colorClass="text-[#2B7EC1]" bgClass="bg-[#2B7EC1]/10" />
              <FeatureCard emoji="📚" title="e-Learning" subtitle="Courses & quizzes" colorClass="text-[#E8A020]" bgClass="bg-[#E8A020]/10" />
            </div>

          </div>

          {/* RIGHT COLUMN - Form */}
          <div className="relative overflow-hidden rounded-[28px] px-8 py-[46px]" style={{
            background: '#FFF8F2',
            boxShadow: '0 20px 56px rgba(160,80,40,0.12), inset 0 2px 0 rgba(255,255,255,0.95)',
          }}>
            <div className="absolute -bottom-20 -left-20 w-[260px] h-[240px] rounded-full pointer-events-none" style={{
              background: 'radial-gradient(ellipse, rgba(244,67,33,0.07) 0%, transparent 60%)',
              filter: 'blur(40px)',
            }} />

            {/* Mobile logo */}
            <div className="lg:hidden flex items-center gap-2.5 mb-6">
              <img src="/lurneax-logo.svg" alt="Lurneax" className="h-9 w-9" />
            </div>

            <div className="mb-7 relative z-10">
              <span className="text-4xl block mb-2.5 animate-wave origin-[70%_70%]">👋</span>
              <h2 className="text-[28px] font-black tracking-tight mb-1" style={{ color: '#2A1810', letterSpacing: '-1px' }}>
                {isForgotPassword ? "Reset password" : "Welcome back!"}
              </h2>
              <p className="text-sm font-semibold" style={{ color: '#8A6050' }}>
                {isForgotPassword
                  ? "Enter your email to receive a password reset link"
                  : "Sign in to your Lurneax workspace"}
              </p>
            </div>


            <form onSubmit={isForgotPassword ? handleForgotPassword : handleSignIn} className="space-y-3.5 relative z-10">
              <div>
                <label htmlFor="email" className="block text-xs font-extrabold mb-1.5 transition-colors" style={{ color: '#2A1810' }}>
                  Email address
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    placeholder="you@schoolola.com.au"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    maxLength={255}
                    autoComplete="email"
                    className="w-full h-[52px] px-4 pr-12 rounded-[14px] border-2 border-transparent text-sm font-semibold outline-none transition-all focus:border-[#F44321] focus:bg-white focus:shadow-[0_0_0_4px_rgba(244,67,33,0.1)]"
                    style={{
                      background: '#F5E6D8',
                      color: '#2A1810',
                      fontFamily: "'Nunito', sans-serif",
                    }}
                  />
                  <div className="absolute right-3.5 top-1/2 -translate-y-1/2" style={{ color: '#C4A898' }}>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                </div>
                {errors.email && <p className="text-xs font-semibold mt-1" style={{ color: '#F44321' }}>{errors.email}</p>}
              </div>

              {!isForgotPassword && (
                <div>
                  <label htmlFor="password" className="block text-xs font-extrabold mb-1.5" style={{ color: '#2A1810' }}>
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      maxLength={100}
                      autoComplete="current-password"
                      className="w-full h-[52px] px-4 pr-12 rounded-[14px] border-2 border-transparent text-sm font-semibold outline-none transition-all focus:border-[#F44321] focus:bg-white focus:shadow-[0_0_0_4px_rgba(244,67,33,0.1)]"
                      style={{
                        background: '#F5E6D8',
                        color: '#2A1810',
                        fontFamily: "'Nunito', sans-serif",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer p-0 flex transition-colors hover:opacity-70"
                      style={{ color: '#C4A898' }}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs font-semibold mt-1" style={{ color: '#F44321' }}>{errors.password}</p>}
                </div>
              )}

              {!isForgotPassword && (
                <div className="flex items-center justify-between py-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="w-[15px] h-[15px]" style={{ accentColor: '#F44321' }} />
                    <span className="text-[13px] font-bold select-none" style={{ color: '#8A6050' }}>Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => { setIsForgotPassword(true); setErrors({}); setPassword(""); }}
                    className="text-[13px] font-bold bg-transparent border-none cursor-pointer hover:opacity-75 transition-opacity"
                    style={{ color: '#F44321' }}
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-14 border-none rounded-2xl text-white text-base font-black cursor-pointer transition-all hover:-translate-y-0.5 hover:scale-[1.01] active:translate-y-px active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #F44321, #FF6B4A)',
                  boxShadow: '0 8px 28px rgba(244,67,33,0.38), inset 0 2px 0 rgba(255,255,255,0.2), inset 0 -2px 0 rgba(0,0,0,0.1)',
                  letterSpacing: '-0.3px',
                  fontFamily: "'Nunito', sans-serif",
                }}
              >
                <span className="absolute inset-0 pointer-events-none" style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%)',
                }} />
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {isLoading
                    ? (isForgotPassword ? "Sending..." : "Signing in...")
                    : (isForgotPassword ? "Send Reset Link" : (
                      <>Let's go! <ArrowRight className="h-4 w-4" /></>
                    ))}
                </span>
              </button>

              {isForgotPassword && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setIsForgotPassword(false); setErrors({}); }}
                    className="text-sm font-bold bg-transparent border-none cursor-pointer hover:opacity-75 transition-opacity"
                    style={{ color: '#8A6050' }}
                  >
                    ← Back to Sign In
                  </button>
                </div>
              )}
            </form>

            {!isForgotPassword && (
              <p className="text-center text-[13px] font-bold mt-5 relative z-10" style={{ color: '#8A6050' }}>
                Powered by the smartest minds in education
              </p>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

function FeatureCard({ emoji, title, subtitle, colorClass, bgClass }: {
  emoji: string; title: string; subtitle: string; colorClass: string; bgClass: string;
}) {
  return (
    <div
      className="flex flex-col gap-1.5 rounded-[20px] px-4 py-4 cursor-default transition-transform hover:-translate-y-1 hover:scale-[1.01]"
      style={{
        background: '#FFF8F2',
        boxShadow: '0 6px 20px rgba(160,80,40,0.08), inset 0 2px 0 rgba(255,255,255,0.9)',
      }}
    >
      <div className={`w-9 h-9 rounded-[10px] flex items-center justify-center text-lg ${bgClass}`}>
        {emoji}
      </div>
      <div className={`text-sm font-black leading-tight tracking-tight ${colorClass}`}>
        {title}
      </div>
      <div className="text-[11px] font-bold" style={{ color: '#8A6050' }}>{subtitle}</div>
    </div>
  );
}

export default LandingPage;
